// TelcoTrack - Construction Diary Entry Modal
// Denní zápis stavebního deníku — edit + confirm (pro stavbyvedoucího).
// Otevírá se ručně ze záložky / přehledu stavebního deníku.

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ToastService } from '../../../../shared/components/ui/toast/toast.service';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import type {
  ConstructionDiary,
  ConstructionDiaryAttachment,
  DiaryAdditionalSignatureConfirm,
  DiaryWeatherCondition,
  DiaryWorkSummaryEntry,
  DiaryWorkSummaryPhoto,
  UpdateConstructionDiaryDto,
} from '../../../../core/models/construction-diary.model';
import { ConstructionDiaryApiService } from '../../../../core/services/construction-diary.service';
import {
  getConstructionDiaryEditWindowDays,
  isConstructionDiaryEntryDateEditable,
} from '../../../../core/utils/construction-diary-edit-window.web';
import {
  buildPredaniOdpovednostiNote,
  buildPredaniPovrchuNote,
  DIARY_SIGNATURE_TEMPLATE_OPTIONS,
  DiaryAdditionalSignatureTemplate,
  diaryProtocolNoteStateOnOpen,
  formatPredaniZastupceLabel,
  shouldReplaceDiaryProtocolNote,
} from '../../../../core/utils/construction-diary-signature-templates.web';
import { AuthService } from '../../../../core/services/auth.service';
import { DiarySignaturePadComponent } from '../diary-signature-pad/diary-signature-pad.component';
import { getObjectTypeName, resolveAttachmentPhaseLabel } from '../../../../shared/utils/object.utils';
import {
  DIARY_WEATHER_CONDITIONS,
  filterWorkSummaryEntriesForDiaryDay,
  formatUserDisplayName,
  getExcavationMaterialName,
  getExcavationSubtypeName,
} from '@core/shared-bridge';
import { formatDiaryEntryDateCs } from '../../../../core/utils/construction-diary-list-period.web';
import { ObjectService } from '../../../../core/services/object.service';
import {
  fileToUploadDataUrl,
  uploadFileNameForCompressedImage,
} from '../../../../core/utils/image-upload-compress.util';
import {
  buildingAnchorFromObject,
  buildWorkGroupDisplayLines,
  buildWorkGroupRoutePolyline,
  dedupeBuildingAnchors,
  DIARY_ADDRESS_ANCHOR_OBJECT_TYPES,
  formatWorkGroupHeadline,
  isExcavationType,
  shouldShowWorkGroupMap,
  workSummaryGroupKey,
  type BuildingAnchor,
  type RoutePoint,
  type WorkGroupDisplayLine,
} from '../../../../core/utils/diary-work-summary-display.util';

/**
 * Seskupené provedené práce pro výpis v šabloně. Vzor = user-activity timeline,
 * ale agregováno per uživatel pro všechny účastníky v polygonu.
 * Jedna skupina = 1 akce (batch commit nebo série per-object commitů v téže minutě).
 */
interface DiaryWorkGroup {
  groupKey: string;
  batchId: string | null;
  changedBy: string;
  changedByName: string | null;
  phaseFrom: string | null;
  phaseTo: string;
  objectType: string;
  /** Nejstarší timestamp ve skupině (ISO-8601). */
  timestamp: string;
  items: DiaryWorkSummaryEntry[];
  /** Kategorizované řádky (Materiál / Povrchy / …) — bez duplicit surface vs note. */
  displayLines: WorkGroupDisplayLine[];
  /** Čitelný nadpis (adresy / souřadnice místo ID). */
  headline: string;
  fromLabel: string | null;
  toLabel: string | null;
  showMap: boolean;
  objectIds: string[];
  routePoints: RoutePoint[];
  photos: DiaryWorkSummaryPhoto[];
}

/** Počet skupin nad tímto prahem → sbalená sekce s přepínačem (parita Android). */
const WORK_SUMMARY_COLLAPSE_THRESHOLD = 3;
/** Počet náhledových karet ve sbaleném stavu. */
const WORK_SUMMARY_PREVIEW_GROUPS = 2;
/** Počet auto pracovníků nad tímto prahem → sbalená sekce (parita Android). */
const AUTO_WORKERS_COLLAPSE_THRESHOLD = 3;
/** Počet náhledových řádků auto pracovníků ve sbaleném stavu. */
const AUTO_WORKERS_PREVIEW_ITEMS = 4;

const WEATHER_OPTIONS: Array<{ value: DiaryWeatherCondition; label: string }> = [
  { value: null, label: '— neurčeno —' },
  ...DIARY_WEATHER_CONDITIONS.map((o) => ({ value: o.value as DiaryWeatherCondition, label: o.label })),
];

@Component({
  selector: 'app-diary-entry-modal',
  templateUrl: './diary-entry-modal.component.html',
  styleUrls: ['./diary-entry-modal.component.scss'],
})
export class DiaryEntryModalComponent implements OnChanges, OnDestroy {
  @ViewChild('sigPad') private readonly sigPad?: DiarySignaturePadComponent;
  @ViewChildren('extraSigPad', { read: DiarySignaturePadComponent })
  private readonly extraSigPads?: QueryList<DiarySignaturePadComponent>;

  @Input() isOpen = false;
  @Input() entry: ConstructionDiary | null = null;
  /** Načítá se plný detail po kliknutí ze seznamu. */
  @Input() detailLoading = false;
  /** Má aktuální uživatel právo potvrdit deník (stavbyvedoucí/admin/manager/superadmin)? */
  @Input() canConfirm = false;

  @Output() closeEvent = new EventEmitter<void>();
  @Output() saved = new EventEmitter<ConstructionDiary>();
  @Output() confirmed = new EventEmitter<ConstructionDiary>();

  readonly weatherOptions = WEATHER_OPTIONS;
  readonly signatureTemplateOptions = DIARY_SIGNATURE_TEMPLATE_OPTIONS;
  readonly editWindowDays = getConstructionDiaryEditWindowDays();

  form: FormGroup;
  isSubmitting = false;
  isConfirming = false;
  isUploadingAttachment = false;
  deletingAttachmentId: string | null = null;
  errorMessage: string | null = null;
  attachments: ConstructionDiaryAttachment[] = [];
  private readonly toast = inject(ToastService);

  /** Po úspěšném uzavření dne automaticky zavře modal (ms). */
  private static readonly CONFIRM_AUTO_CLOSE_MS = 3000;
  private confirmAutoCloseHandle: ReturnType<typeof setTimeout> | null = null;
  /** Poslední auto-generovaný text mustru per řádek (pro rozlišení ruční úpravy). */
  private readonly additionalSigLastAutoNote = new WeakMap<FormGroup, string>();
  private readonly additionalSigRowSubs = new Map<FormGroup, Subscription[]>();
  private obecAutoNoteSub?: Subscription;
  private buildingAnchors: BuildingAnchor[] = [];
  private buildingsPolygonId: string | null = null;
  private buildingsLoadSub: Subscription | null = null;
  /** Cache — getter by každý CD cyklus ničil mapové komponenty v *ngFor. */
  workSummaryGroups: DiaryWorkGroup[] = [];
  /** Ve sbaleném stavu false; platí jen když je více než WORK_SUMMARY_COLLAPSE_THRESHOLD skupin. */
  workSummaryExpanded = false;
  /** Pracovníci zkopírovaní ze specifikace — uživatel je může v tento den odebrat. */
  autoWorkersList: Array<{ email?: string | null; name?: string | null; role?: string | null }> = [];
  autoWorkersExpanded = false;
  selectedWorkPhoto: DiaryWorkSummaryPhoto | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly api: ConstructionDiaryApiService,
    private readonly cdr: ChangeDetectorRef,
    private readonly objectService: ObjectService,
    private readonly authService: AuthService,
  ) {
    this.form = this.fb.group({
      // Identifikace stavby (obvykle předvyplněno z polygon.metadata.diaryConfig)
      constructionName: ['', [Validators.maxLength(255)]],
      stavebnik: ['', [Validators.maxLength(255)]],
      projektant: ['', [Validators.maxLength(255)]],
      zhotovitel: ['', [Validators.maxLength(255)]],
      stavebniPovoleniCislo: ['', [Validators.maxLength(100)]],
      stavbyveduciName: ['', [Validators.maxLength(255)]],
      obec: ['', [Validators.maxLength(255)]],
      registracniCisloProjektu: ['', [Validators.maxLength(100)]],

      // Počasí
      weatherTempMorningC: [null as number | null],
      weatherTempAfternoonC: [null as number | null],
      weatherCondition: [null as DiaryWeatherCondition],
      weatherNote: ['', [Validators.maxLength(500)]],

      // Pracovníci / mechanizace / subdodavatelé — workersManual = volný text (parita Android)
      workersManual: ['', [Validators.maxLength(4000)]],
      machinery: ['', [Validators.maxLength(2000)]],
      subcontractors: ['', [Validators.maxLength(2000)]],

      // Práce / odchylky / poznámky
      workSummaryNote: ['', [Validators.maxLength(4000)]],
      supervisorInstructions: ['', [Validators.maxLength(4000)]],
      deviations: ['', [Validators.maxLength(4000)]],
      safetyNotes: ['', [Validators.maxLength(4000)]],
      notes: ['', [Validators.maxLength(4000)]],
      additionalSignatures: this.fb.array<FormGroup>([]),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entry'] || changes['detailLoading']) {
      if (this.entry && !this.detailLoading) {
        const prev = changes['entry']?.previousValue as ConstructionDiary | null | undefined;
        const curr = this.entry;
        const prevId = prev?.id ?? null;
        const currId = curr?.id ?? null;
        // List opens with a same-id stub while detailLoading=true, then replaces
        // with GET detail. Treat detail-load finish as a pad reset so a saved
        // draft signature hydrates (prevId === currId would otherwise skip it).
        const finishedDetailLoad =
          Boolean(changes['detailLoading']) &&
          changes['detailLoading'].previousValue === true &&
          changes['detailLoading'].currentValue === false;
        const resetSignaturePad =
          Boolean(curr && !curr.isLocked) &&
          (changes['entry']?.isFirstChange() || prevId !== currId || finishedDetailLoad);
        this.populate(this.entry, resetSignaturePad);
      }
    }
    if (changes['isOpen'] && !this.isOpen) {
      this.clearConfirmAutoClose();
      this.errorMessage = null;
    }
  }

  ngOnDestroy(): void {
    this.clearConfirmAutoClose();
    this.teardownAdditionalSigAutoNoteSync();
    this.buildingsLoadSub?.unsubscribe();
  }

  private clearConfirmAutoClose(): void {
    if (this.confirmAutoCloseHandle != null) {
      clearTimeout(this.confirmAutoCloseHandle);
      this.confirmAutoCloseHandle = null;
    }
  }

  get isLocked(): boolean {
    return !!this.entry?.isLocked;
  }

  /** Neuzamčený záznam v rámci uzavíracího okna (výchozí 7 dní). */
  get canEditEntry(): boolean {
    if (!this.entry || this.entry.isLocked) return false;
    return isConstructionDiaryEntryDateEditable(this.entry.entryDate);
  }

  get showConfirmSection(): boolean {
    return this.canEditEntry && this.canConfirm;
  }

  /** Další podpisy jdou uložit Uložit (draft), nejen při Potvrdit. */
  get showExtraSignaturesEditor(): boolean {
    return this.canEditEntry;
  }

  /** Neuzamčený záznam v edit okně, nebo uzamčený (dodatek). */
  get canEditForm(): boolean {
    if (!this.entry) return false;
    return this.canEditEntry || this.isLocked;
  }

  weatherConditionLabel(condition: DiaryWeatherCondition | undefined | null): string {
    return WEATHER_OPTIONS.find((o) => o.value === condition)?.label ?? '—';
  }

  get additionalSigRows(): FormArray<FormGroup> {
    return this.form.get('additionalSignatures') as FormArray<FormGroup>;
  }

  get autoWorkersCollapsible(): boolean {
    return this.autoWorkersList.length > AUTO_WORKERS_COLLAPSE_THRESHOLD;
  }

  get displayedAutoWorkers(): Array<{ email?: string | null; name?: string | null; role?: string | null }> {
    if (this.canEditEntry || !this.autoWorkersCollapsible || this.autoWorkersExpanded) {
      return this.autoWorkersList;
    }
    return this.autoWorkersList.slice(0, AUTO_WORKERS_PREVIEW_ITEMS);
  }

  get autoWorkersHiddenCount(): number {
    if (!this.autoWorkersCollapsible || this.autoWorkersExpanded) return 0;
    return Math.max(0, this.autoWorkersList.length - AUTO_WORKERS_PREVIEW_ITEMS);
  }

  toggleAutoWorkersExpanded(): void {
    this.autoWorkersExpanded = !this.autoWorkersExpanded;
  }

  removeAutoWorker(index: number): void {
    if (!this.canEditEntry || index < 0 || index >= this.autoWorkersList.length) return;
    this.autoWorkersList = this.autoWorkersList.filter((_, i) => i !== index);
  }

  formatAutoWorkerLine(worker: {
    email?: string | null;
    name?: string | null;
    role?: string | null;
  }): string {
    const name = (worker.name ?? '').trim() || (worker.email ?? '').trim() || 'Neznámý pracovník';
    const role = (worker.role ?? '').trim();
    return role ? `${name} · ${role}` : name;
  }

  trackWorkGroup(_index: number, group: DiaryWorkGroup): string {
    return group.groupKey;
  }

  get workSummaryCollapsible(): boolean {
    return this.workSummaryGroups.length > WORK_SUMMARY_COLLAPSE_THRESHOLD;
  }

  get displayedWorkSummaryGroups(): DiaryWorkGroup[] {
    if (!this.workSummaryCollapsible || this.workSummaryExpanded) {
      return this.workSummaryGroups;
    }
    return this.workSummaryGroups.slice(0, WORK_SUMMARY_PREVIEW_GROUPS);
  }

  get workSummaryHiddenGroupCount(): number {
    return Math.max(0, this.workSummaryGroups.length - WORK_SUMMARY_PREVIEW_GROUPS);
  }

  toggleWorkSummaryExpanded(): void {
    this.workSummaryExpanded = !this.workSummaryExpanded;
  }

  /**
   * Work summary agregovaný jako user-activity timeline, ale pro všechny účastníky.
   * Primární klíč: `batchId` (partial-advance / multi-segment commit) → jedna karta + jedna mapa.
   * Bez batchu: jeden řádek historie = jeden objekt → vlastní karta + mapa.
   */
  private rebuildWorkSummaryGroups(): void {
    const items = filterWorkSummaryEntriesForDiaryDay(
      this.entry?.workSummaryAuto,
      this.entry?.entryDate,
    );
    if (!items.length) {
      this.workSummaryGroups = [];
      this.workSummaryExpanded = false;
      return;
    }
    const map = new Map<string, DiaryWorkGroup>();
    for (const it of items) {
      const tsMs = new Date(it.timestamp).getTime();
      const key = workSummaryGroupKey(it);
      const existing = map.get(key);
      if (existing) {
        existing.items.push(it);
        if (tsMs < new Date(existing.timestamp).getTime()) existing.timestamp = it.timestamp;
      } else {
        map.set(key, {
          groupKey: key,
          batchId: it.batchId ?? null,
          changedBy: it.changedBy,
          changedByName: it.changedByName ?? null,
          phaseFrom: it.phaseFrom ?? null,
          phaseTo: it.phaseTo,
          objectType: it.objectType,
          timestamp: it.timestamp,
          items: [it],
          displayLines: [],
          headline: '',
          fromLabel: null,
          toLabel: null,
          showMap: false,
          objectIds: [],
          routePoints: [],
          photos: [],
        });
      }
    }
    const dedup = (arr: Array<string | null | undefined>): string[] => {
      const set = new Set<string>();
      for (const v of arr) if (typeof v === 'string' && v.trim()) set.add(v.trim());
      return [...set];
    };
    const groups = [...map.values()];
    for (const g of groups) {
      const surfaces = dedup(g.items.map((s) => s.surface));
      const notes = dedup(g.items.map((s) => s.note));
      g.displayLines = buildWorkGroupDisplayLines(
        surfaces,
        notes,
        g.objectType,
        (raw) => this.displaySurface(raw),
      );
      g.objectIds = [...new Set(g.items.map((s) => s.objectId).filter(Boolean))];
      g.routePoints = buildWorkGroupRoutePolyline(g.items);
      const phaseLabel = this.displayPhase(g.objectType, g.phaseTo, g.phaseFrom);
      const formatted = formatWorkGroupHeadline(
        { ...g, phaseTo: g.phaseTo },
        this.buildingAnchors,
        phaseLabel,
      );
      g.headline = formatted.headline;
      g.fromLabel = formatted.fromLabel;
      g.toLabel = formatted.toLabel;
      g.showMap = shouldShowWorkGroupMap(g);
      g.photos = this.mergeGroupPhotos(g.items);
    }
    this.workSummaryGroups = groups.sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );
    this.workSummaryExpanded = false;
  }

  displayPersonName(name: string | null | undefined, fallbackEmail: string): string {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    return trimmed || fallbackEmail;
  }

  displayObjectType(type: string | null | undefined): string {
    return type ? getObjectTypeName(type) : 'Objekt';
  }

  displayPhase(objectType: string, phase: string | null | undefined, _fromPhase?: string | null): string {
    if (!phase) return '—';
    // Same as PDF/backend `displayPhaseLabel` — resolves `*.MILESTONE.*` + linear WF codes.
    return resolveAttachmentPhaseLabel(phase, objectType) || phase;
  }

  workGroupIsExcavation(group: DiaryWorkGroup): boolean {
    return isExcavationType(group.objectType);
  }

  private mergeGroupPhotos(items: DiaryWorkSummaryEntry[]): DiaryWorkSummaryPhoto[] {
    const seen = new Set<string>();
    const out: DiaryWorkSummaryPhoto[] = [];
    for (const item of items) {
      for (const photo of item.photos ?? []) {
        if (!photo?.id || seen.has(photo.id)) continue;
        seen.add(photo.id);
        out.push(photo);
      }
    }
    return out;
  }

  workPhotoSrc(photo: DiaryWorkSummaryPhoto): string {
    const path = photo.url?.trim() ?? '';
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) {
      return path;
    }
    return path.startsWith('/') ? path : `/${path}`;
  }

  openWorkPhotoModal(photo: DiaryWorkSummaryPhoto, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    this.selectedWorkPhoto = photo;
  }

  closeWorkPhotoModal(): void {
    this.selectedWorkPhoto = null;
  }

  onWorkPhotoError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (img) img.style.display = 'none';
  }

  displaySurface(surface: string): string {
    const parts = surface.split('·').map((p) => p.trim()).filter(Boolean);
    if (!parts.length) return surface;
    const [material, subtype] = parts;
    return [getExcavationMaterialName(material), subtype ? getExcavationSubtypeName(subtype) : null]
      .filter(Boolean)
      .join(' · ');
  }

  private loadBuildingAnchors(polygonId: string): void {
    if (!polygonId) {
      this.buildingAnchors = [];
      this.buildingsPolygonId = null;
      return;
    }
    if (this.buildingsPolygonId === polygonId && this.buildingAnchors.length) return;
    this.buildingsPolygonId = polygonId;
    this.buildingsLoadSub?.unsubscribe();
    this.buildingAnchors = [];
    const requests = DIARY_ADDRESS_ANCHOR_OBJECT_TYPES.map((objectType) =>
      this.objectService.getObjectsInPolygon(polygonId, objectType).pipe(take(1)),
    );
    this.buildingsLoadSub = forkJoin(requests).subscribe({
      next: (results) => {
        const objects = results.flat();
        this.buildingAnchors = dedupeBuildingAnchors(
          objects
            .map((o) => buildingAnchorFromObject(o))
            .filter((a): a is BuildingAnchor => a != null),
        );
        this.rebuildWorkSummaryGroups();
        this.cdr.markForCheck();
      },
      error: () => {
        this.buildingAnchors = [];
        this.rebuildWorkSummaryGroups();
        this.cdr.markForCheck();
      },
    });
  }

  private populate(entry: ConstructionDiary | null, resetSignaturePad: boolean): void {
    if (!entry) {
      this.form.reset();
      this.autoWorkersList = [];
      this.autoWorkersExpanded = false;
      this.clearAdditionalSignatureRows();
      this.attachments = [];
      this.buildingAnchors = [];
      this.buildingsPolygonId = null;
      this.buildingsLoadSub?.unsubscribe();
      this.workSummaryGroups = [];
      this.workSummaryExpanded = false;
      return;
    }
    this.form.reset({
      constructionName: entry.constructionName ?? '',
      stavebnik: entry.stavebnik ?? '',
      projektant: entry.projektant ?? '',
      zhotovitel: entry.zhotovitel ?? '',
      stavebniPovoleniCislo: entry.stavebniPovoleniCislo ?? '',
      stavbyveduciName: entry.stavbyveduciName ?? '',
      obec: entry.obec ?? '',
      registracniCisloProjektu: entry.registracniCisloProjektu ?? '',
      weatherTempMorningC: entry.weatherTempMorningC ?? null,
      weatherTempAfternoonC: entry.weatherTempAfternoonC ?? null,
      weatherCondition: entry.weatherCondition ?? null,
      weatherNote: entry.weatherNote ?? '',
      workersManual: entry.workersManual ?? '',
      machinery: entry.machinery ?? '',
      subcontractors: entry.subcontractors ?? '',
      workSummaryNote: entry.workSummaryNote ?? '',
      supervisorInstructions: entry.supervisorInstructions ?? '',
      deviations: entry.deviations ?? '',
      safetyNotes: entry.safetyNotes ?? '',
      notes: entry.notes ?? '',
    });
    this.autoWorkersList = (entry.workersAuto ?? []).filter(
      (w) => (w.email ?? '').trim() || (w.name ?? '').trim() || (w.role ?? '').trim(),
    );
    this.autoWorkersExpanded = false;
    this.hydrateAdditionalSignatures(entry);
    this.attachments = entry.attachments ?? [];
    this.loadAttachments(entry.id);
    this.loadBuildingAnchors(entry.polygonId);
    this.rebuildWorkSummaryGroups();
    if (this.canEditForm) {
      this.form.enable({ emitEvent: false });
    } else {
      this.form.disable({ emitEvent: false });
    }
    if (!entry.isLocked && resetSignaturePad) {
      setTimeout(() => {
        this.sigPad?.clear();
        const url = entry.signaturePngBase64?.trim();
        if (url && this.sigPad) {
          this.sigPad.loadFromDataUrl(url);
        }
      }, 0);
    }
  }

  private clearAdditionalSignatureRows(): void {
    this.teardownAdditionalSigAutoNoteSync();
    this.additionalSigRows.clear();
  }

  addAdditionalSigRow(): void {
    if (this.additionalSigRows.length >= 15) return;
    const row = this.createAdditionalSigRow();
    this.additionalSigRows.push(row);
    this.wireAdditionalSigRow(row);
    this.ensureObecAutoNoteSubscription();
  }

  onExtraSignatureChange(index: number, hasInk: boolean): void {
    const row = this.additionalSigRows.at(index) as FormGroup | undefined;
    if (!row) return;
    if (!hasInk) {
      row.patchValue({ signaturePngBase64: '' }, { emitEvent: false });
      return;
    }
    const pad = this.extraSigPads?.toArray()?.[index];
    if (pad && !pad.isEmpty()) {
      row.patchValue({ signaturePngBase64: pad.toPngDataUrl() }, { emitEvent: false });
    }
  }

  removeAdditionalSigRow(index: number): void {
    const row = this.additionalSigRows.at(index) as FormGroup;
    this.unwireAdditionalSigRow(row);
    this.additionalSigRows.removeAt(index);
  }

  private createAdditionalSigRow(seed?: {
    firstName?: string;
    lastName?: string;
    place?: string;
    role?: string;
    note?: string;
    signaturePngBase64?: string;
  }): FormGroup {
    const defaultDate = this.entry?.entryDate ?? '';
    const obec = toTrim(this.form.get('obec')?.value);
    const user = this.authService.getCurrentUser();
    const predavajiciName = formatUserDisplayName({
      firstName: user?.firstName,
      lastName: user?.lastName,
      displayName: user?.displayName,
      email: user?.email,
    });
    return this.fb.group({
      templateType: ['predani_povrchu' as DiaryAdditionalSignatureTemplate],
      predavajiciZastupce: [predavajiciName, [Validators.maxLength(255)]],
      predavajiciRole: ['', [Validators.maxLength(120)]],
      predanyUsek: ['', [Validators.maxLength(4000)]],
      firstName: [seed?.firstName ?? '', [Validators.maxLength(100)]],
      lastName: [seed?.lastName ?? '', [Validators.maxLength(100)]],
      place: [seed?.place ?? obec, [Validators.maxLength(255)]],
      role: [seed?.role ?? '', [Validators.maxLength(150)]],
      handoverDate: [defaultDate, [Validators.maxLength(10)]],
      effectiveDate: [defaultDate, [Validators.maxLength(10)]],
      protocolDate: [defaultDate, [Validators.maxLength(10)]],
      periodFrom: [defaultDate, [Validators.maxLength(10)]],
      periodTo: [defaultDate, [Validators.maxLength(10)]],
      reason: ['', [Validators.maxLength(4000)]],
      defectsFound: [false],
      defectsDescription: ['', [Validators.maxLength(4000)]],
      defectsRemediationDeadline: ['', [Validators.maxLength(500)]],
      note: [seed?.note ?? '', [Validators.maxLength(50000)]],
      /** Uložené PNG — fallback když pad ještě nenačetl kresbu (async Image). */
      signaturePngBase64: [seed?.signaturePngBase64 ?? ''],
    });
  }

  /** Obnoví draft další podpisy z GET (včetně PNG bez jména/role). */
  private hydrateAdditionalSignatures(entry: ConstructionDiary): void {
    this.clearAdditionalSignatureRows();
    for (const s of entry.additionalSignatures ?? []) {
      const firstName = toTrim(s.firstName);
      const lastName = toTrim(s.lastName);
      const place = toTrim(s.place);
      const role = toTrim(s.role);
      const note = toTrim(s.note);
      const signaturePngBase64 = toTrim(s.signaturePngBase64);
      if (!firstName && !lastName && !place && !role && !note && !signaturePngBase64) continue;
      const row = this.createAdditionalSigRow({
        firstName,
        lastName,
        place,
        role,
        note,
        signaturePngBase64,
      });
      this.additionalSigRows.push(row);
      this.wireAdditionalSigRow(row, { skipInitialAutoNote: true });
    }
    this.ensureObecAutoNoteSubscription();
    setTimeout(() => this.loadExtraSignaturePadsFromRows(), 0);
  }

  private loadExtraSignaturePadsFromRows(): void {
    this.cdr.detectChanges();
    const pads = this.extraSigPads?.toArray() ?? [];
    for (let i = 0; i < this.additionalSigRows.length; i++) {
      const stored = toTrim(this.additionalSigRows.at(i).get('signaturePngBase64')?.value);
      const pad = pads[i];
      if (pad && stored) {
        pad.loadFromDataUrl(stored);
      }
    }
  }

  isPredaniPovrchuTemplate(row: AbstractControl): boolean {
    return (row as FormGroup).get('templateType')?.value === 'predani_povrchu';
  }

  isPredaniOdpovednostiTemplate(row: AbstractControl): boolean {
    return (row as FormGroup).get('templateType')?.value === 'predani_odpovednosti';
  }

  isJineSignatureTemplate(row: AbstractControl): boolean {
    return (row as FormGroup).get('templateType')?.value === 'jine';
  }

  private teardownAdditionalSigAutoNoteSync(): void {
    for (const row of this.additionalSigRowSubs.keys()) {
      this.unwireAdditionalSigRow(row);
    }
    this.obecAutoNoteSub?.unsubscribe();
    this.obecAutoNoteSub = undefined;
  }

  private ensureObecAutoNoteSubscription(): void {
    if (this.obecAutoNoteSub || this.additionalSigRows.length === 0) return;
    this.obecAutoNoteSub = this.form.get('obec')!.valueChanges.subscribe(() => {
      for (const row of this.additionalSigRows.controls) {
        this.syncAutoNoteForRow(row as FormGroup);
      }
    });
  }

  private unwireAdditionalSigRow(row: FormGroup): void {
    for (const sub of this.additionalSigRowSubs.get(row) ?? []) {
      sub.unsubscribe();
    }
    this.additionalSigRowSubs.delete(row);
    this.additionalSigLastAutoNote.delete(row);
  }

  private wireAdditionalSigRow(row: FormGroup, opts?: { skipInitialAutoNote?: boolean }): void {
    this.unwireAdditionalSigRow(row);
    const subs: Subscription[] = [];

    subs.push(
      row.get('templateType')!.valueChanges.subscribe((type: DiaryAdditionalSignatureTemplate) => {
        if (type === 'predani_povrchu' || type === 'predani_odpovednosti') {
          this.applyAutoNoteToRow(row, true);
        } else {
          this.additionalSigLastAutoNote.delete(row);
        }
      }),
    );

    subs.push(
      row.get('handoverDate')!.valueChanges.subscribe((date: string) => {
        const d = toTrim(date);
        row.patchValue({ effectiveDate: d, protocolDate: d }, { emitEvent: false });
        this.syncAutoNoteForRow(row);
      }),
    );

    for (const field of [
      'predanyUsek',
      'predavajiciZastupce',
      'predavajiciRole',
      'firstName',
      'lastName',
      'place',
      'effectiveDate',
      'protocolDate',
      'periodFrom',
      'periodTo',
      'reason',
      'defectsFound',
      'defectsDescription',
      'defectsRemediationDeadline',
    ] as const) {
      subs.push(row.get(field)!.valueChanges.subscribe(() => this.syncAutoNoteForRow(row)));
    }

    this.additionalSigRowSubs.set(row, subs);
    if (opts?.skipInitialAutoNote) {
      const saved = toTrim(row.get('note')?.value);
      const generated = this.generateAutoNoteForRow(row);
      const state = diaryProtocolNoteStateOnOpen({ savedNote: saved, generatedNote: generated });
      row.patchValue({ note: state.note }, { emitEvent: false });
      this.additionalSigLastAutoNote.set(row, state.lastAutoNote);
    } else {
      this.applyAutoNoteToRow(row, true);
    }
  }

  private generateAutoNoteForRow(row: FormGroup): string {
    const templateType = row.get('templateType')?.value as DiaryAdditionalSignatureTemplate;
    if (templateType === 'jine') {
      return toTrim(row.get('note')?.value);
    }
    if (templateType === 'predani_odpovednosti') {
      const predavajici = formatPredaniZastupceLabel(
        toTrim(row.get('predavajiciZastupce')?.value),
        toTrim(row.get('predavajiciRole')?.value),
      );
      const stavbyvedouci =
        `${toTrim(row.get('firstName')?.value)} ${toTrim(row.get('lastName')?.value)}`.trim();
      return buildPredaniOdpovednostiNote({
        stavbaLabel: toTrim(this.form.get('constructionName')?.value) || '…',
        predavajici,
        stavbyvedouci,
        periodFrom: toTrim(row.get('periodFrom')?.value),
        periodTo: toTrim(row.get('periodTo')?.value),
        reason: toTrim(row.get('reason')?.value),
        protocolDate:
          toTrim(row.get('handoverDate')?.value) || toTrim(row.get('protocolDate')?.value),
        place: toTrim(row.get('place')?.value),
      });
    }
    const obec = toTrim(this.form.get('obec')?.value);
    const sharedDate =
      toTrim(row.get('handoverDate')?.value) ||
      toTrim(row.get('effectiveDate')?.value) ||
      toTrim(row.get('protocolDate')?.value);
    return buildPredaniPovrchuNote({
      obec,
      effectiveDate: sharedDate,
      protocolDate: sharedDate,
      defectsFound: !!row.get('defectsFound')?.value,
      defectsDescription: toTrim(row.get('defectsDescription')?.value),
      defectsRemediationDeadline: toTrim(row.get('defectsRemediationDeadline')?.value),
      predanyUsek: toTrim(row.get('predanyUsek')?.value),
    });
  }

  /** Přepíše text mustru jen pokud uživatel neupravil ručně (nebo při vynucení). */
  private applyAutoNoteToRow(row: FormGroup, force = false): void {
    const templateType = row.get('templateType')?.value as DiaryAdditionalSignatureTemplate;
    if (templateType !== 'predani_povrchu' && templateType !== 'predani_odpovednosti') return;
    const auto = this.generateAutoNoteForRow(row);
    const current = toTrim(row.get('note')?.value);
    const lastAuto = this.additionalSigLastAutoNote.get(row) ?? '';
    if (shouldReplaceDiaryProtocolNote({ currentNote: current, lastAutoNote: lastAuto, force })) {
      row.patchValue({ note: auto }, { emitEvent: false });
      this.additionalSigLastAutoNote.set(row, auto);
    }
  }

  private syncAutoNoteForRow(row: FormGroup): void {
    this.applyAutoNoteToRow(row, false);
  }

  private buildNoteForAdditionalSignatureRow(
    row: FormGroup,
    rowIndex: number,
  ): string | null {
    const templateType = row.get('templateType')?.value as DiaryAdditionalSignatureTemplate;
    const note = toTrim(row.get('note')?.value);
    if (!note) {
      this.errorMessage =
        templateType === 'jine'
          ? `Další podpis #${rowIndex + 1}: vyplňte vlastní text, nebo řádek odeberte.`
          : templateType === 'predani_odpovednosti'
            ? `Další podpis #${rowIndex + 1}: vyplňte text potvrzení, nebo řádek odeberte.`
            : `Další podpis #${rowIndex + 1}: vyplňte text protokolu, nebo řádek odeberte.`;
      return null;
    }

    if (templateType === 'predani_odpovednosti') {
      const periodFrom = toTrim(row.get('periodFrom')?.value);
      const periodTo = toTrim(row.get('periodTo')?.value);
      const reason = toTrim(row.get('reason')?.value);
      const protocolDate =
        toTrim(row.get('handoverDate')?.value) || toTrim(row.get('protocolDate')?.value);
      if (!periodFrom || !periodTo) {
        this.errorMessage = `Další podpis #${rowIndex + 1}: vyplňte období od a do.`;
        return null;
      }
      if (!reason) {
        this.errorMessage = `Další podpis #${rowIndex + 1}: vyplňte důvod / rozsah prací bez dozoru.`;
        return null;
      }
      if (!protocolDate) {
        this.errorMessage = `Další podpis #${rowIndex + 1}: vyplňte datum potvrzení.`;
        return null;
      }
      return note;
    }

    if (templateType !== 'predani_povrchu') {
      return note;
    }

    const obec = toTrim(this.form.get('obec')?.value);
    if (!obec) {
      this.errorMessage = `Další podpis #${rowIndex + 1}: vyplňte obec v identifikaci stavby.`;
      return null;
    }
    const sharedDate =
      toTrim(row.get('handoverDate')?.value) ||
      toTrim(row.get('effectiveDate')?.value) ||
      toTrim(row.get('protocolDate')?.value);
    if (!sharedDate) {
      this.errorMessage = `Další podpis #${rowIndex + 1}: vyplňte datum protokolu.`;
      return null;
    }
    const defectsFound = !!row.get('defectsFound')?.value;
    if (defectsFound) {
      const defectsDescription = toTrim(row.get('defectsDescription')?.value);
      const defectsRemediationDeadline = toTrim(row.get('defectsRemediationDeadline')?.value);
      if (!defectsDescription || !defectsRemediationDeadline) {
        this.errorMessage = `Další podpis #${rowIndex + 1}: při zjištěných závadách vyplňte popis i termín odstranění.`;
        return null;
      }
    }
    return note;
  }

  /**
   * Sestaví pole pro confirm; prázdné řádky přeskočí. Při chybě nastaví errorMessage a vrátí null.
   */
  private buildAdditionalSignaturesForConfirm(): DiaryAdditionalSignatureConfirm[] | null {
    this.cdr.detectChanges();
    const pads = this.extraSigPads?.toArray() ?? [];
    const n = this.additionalSigRows.length;
    if (pads.length !== n) {
      this.errorMessage = 'Obnovte prosím stránku a zkuste potvrzení znovu.';
      return null;
    }
    const out: DiaryAdditionalSignatureConfirm[] = [];
    for (let i = 0; i < n; i++) {
      const g = this.additionalSigRows.at(i) as FormGroup;
      const v = g.getRawValue() as {
        templateType: DiaryAdditionalSignatureTemplate;
        firstName: string;
        lastName: string;
        place: string;
        role: string;
        effectiveDate: string;
        protocolDate: string;
        defectsFound: boolean;
        defectsDescription: string;
        defectsRemediationDeadline: string;
        note: string;
      };
      const firstName = toTrim(v.firstName);
      const lastName = toTrim(v.lastName);
      const place = toTrim(v.place);
      const role = toTrim(v.role);
      const pad = pads[i];
      const storedPng = toTrim(g.get('signaturePngBase64')?.value);
      const padHasInk = Boolean(pad && !pad.isEmpty());
      const signaturePngBase64 = padHasInk && pad ? pad.toPngDataUrl() : storedPng;
      const hasAnyField =
        firstName ||
        lastName ||
        place ||
        role ||
        toTrim(v.note) ||
        toTrim(v.defectsDescription) ||
        toTrim(v.defectsRemediationDeadline) ||
        padHasInk ||
        Boolean(storedPng);
      if (!hasAnyField) continue;
      if (!firstName || !lastName || !place || !role) {
        this.errorMessage = `Další podpis #${i + 1}: vyplňte jméno, příjmení, místo a roli, nebo řádek odeberte.`;
        return null;
      }
      if (!signaturePngBase64) {
        this.errorMessage = `Další podpis #${i + 1}: chybí podpis v poli.`;
        return null;
      }
      const note = this.buildNoteForAdditionalSignatureRow(g, i);
      if (note === null) return null;
      out.push({
        firstName,
        lastName,
        place,
        role,
        note,
        signaturePngBase64,
      });
    }
    return out;
  }

  onAttachmentFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!this.entry || !files.length || this.isUploadingAttachment) return;

    this.isUploadingAttachment = true;
    this.errorMessage = null;
    this.uploadAttachmentFiles(this.entry.id, files)
      .then(() => {
        this.toast.showSuccess(
          files.length > 1 ? `Nahráno ${files.length} příloh.` : 'Příloha nahrána.',
        );
        this.loadAttachments(this.entry?.id ?? '');
      })
      .catch((err: unknown) => {
        this.errorMessage = err instanceof Error ? err.message : 'Nahrání přílohy selhalo.';
      })
      .finally(() => {
        this.isUploadingAttachment = false;
      });
  }

  deleteAttachment(attachment: ConstructionDiaryAttachment): void {
    if (!this.entry || this.deletingAttachmentId || !this.canDeleteAttachment(attachment)) return;
    this.deletingAttachmentId = attachment.id;
    this.errorMessage = null;
    this.api.deleteAttachment(this.entry.id, attachment.id).subscribe({
      next: () => {
        this.attachments = this.attachments.filter((item) => item.id !== attachment.id);
        this.deletingAttachmentId = null;
        this.toast.showSuccess('Příloha smazána.');
      },
      error: (err) => {
        this.deletingAttachmentId = null;
        this.errorMessage = err?.message || 'Smazání přílohy selhalo.';
      },
    });
  }

  downloadAttachment(attachment: ConstructionDiaryAttachment): void {
    this.api.downloadAttachment(attachment.id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.originalName || 'priloha';
        link.click();
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        this.errorMessage = err?.message || 'Stažení přílohy selhalo.';
      },
    });
  }

  formatBytes(value: number | null | undefined): string {
    const size = Number(value ?? 0);
    if (!Number.isFinite(size) || size <= 0) return '0 B';
    if (size < 1024) return `${Math.round(size)} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} kB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  canDeleteAttachment(attachment: ConstructionDiaryAttachment): boolean {
    return attachment.source !== 'polygon_document';
  }

  private loadAttachments(entryId: string): void {
    if (!entryId) return;
    this.api.listAttachments(entryId).subscribe({
      next: (attachments) => {
        this.attachments = attachments;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.errorMessage = err?.message || 'Nepodařilo se načíst přílohy.';
        this.cdr.markForCheck();
      },
    });
  }

  private async uploadAttachmentFiles(entryId: string, files: File[]): Promise<void> {
    for (const file of files) {
      const dataUrl = await fileToUploadDataUrl(file);
      const uploadName =
        file.type.startsWith('image/') && file.type !== 'image/svg+xml'
          ? uploadFileNameForCompressedImage(file.name)
          : file.name;
      await new Promise<void>((resolve, reject) => {
        this.api.uploadAttachment(entryId, dataUrl, uploadName).subscribe({
          next: () => resolve(),
          error: (err) => reject(err),
        });
      });
    }
  }

  /** Sestaví payload jen z vyplněných polí; prázdné řetězce posíláme jako prázdné (BE je převede na NULL/trim). */
  private buildPayload(): UpdateConstructionDiaryDto {
    const v = this.form.getRawValue();
    const payload: UpdateConstructionDiaryDto = {
      constructionName: toTrim(v.constructionName),
      stavebnik: toTrim(v.stavebnik),
      projektant: toTrim(v.projektant),
      zhotovitel: toTrim(v.zhotovitel),
      stavebniPovoleniCislo: toTrim(v.stavebniPovoleniCislo),
      stavbyveduciName: toTrim(v.stavbyveduciName),
      obec: toTrim(v.obec),
      registracniCisloProjektu: toTrim(v.registracniCisloProjektu),
      weatherTempMorningC: toFiniteNumber(v.weatherTempMorningC),
      weatherTempAfternoonC: toFiniteNumber(v.weatherTempAfternoonC),
      weatherCondition: v.weatherCondition ?? null,
      weatherNote: toTrim(v.weatherNote),
      workersAuto: this.canEditEntry
        ? this.autoWorkersList.map((worker) => ({
            email: (worker.email ?? '').trim(),
            name: (worker.name ?? '').trim(),
            role: (worker.role ?? '').trim(),
          }))
        : undefined,
      workersManual: toTrim(v.workersManual),
      machinery: toTrim(v.machinery),
      subcontractors: toTrim(v.subcontractors),
      workSummaryNote: toTrim(v.workSummaryNote),
      supervisorInstructions: toTrim(v.supervisorInstructions),
      deviations: toTrim(v.deviations),
      safetyNotes: toTrim(v.safetyNotes),
      notes: toTrim(v.notes),
    };
    if (this.sigPad && !this.sigPad.isEmpty()) {
      payload.signaturePngBase64 = this.sigPad.toPngDataUrl();
    }
    payload.additionalSignatures = this.buildAdditionalSignaturesForSave();
    return payload;
  }

  /**
   * Uložit: další podpisy bez validace jména/role. Prázdné řádky přeskočí.
   * Potvrdit dál používá {@link buildAdditionalSignaturesForConfirm}.
   */
  private buildAdditionalSignaturesForSave(): NonNullable<UpdateConstructionDiaryDto['additionalSignatures']> {
    this.cdr.detectChanges();
    const pads = this.extraSigPads?.toArray() ?? [];
    const n = this.additionalSigRows.length;
    const out: NonNullable<UpdateConstructionDiaryDto['additionalSignatures']> = [];
    for (let i = 0; i < n; i++) {
      const g = this.additionalSigRows.at(i) as FormGroup;
      const v = g.getRawValue() as {
        firstName: string;
        lastName: string;
        place: string;
        role: string;
        note: string;
      };
      const firstName = toTrim(v.firstName);
      const lastName = toTrim(v.lastName);
      const place = toTrim(v.place);
      const role = toTrim(v.role);
      const note = toTrim(v.note);
      const pad = pads[i];
      const stored = toTrim(g.get('signaturePngBase64')?.value);
      const fromPad = pad && !pad.isEmpty() ? pad.toPngDataUrl() : '';
      const signaturePngBase64 = fromPad || stored;
      if (!firstName && !lastName && !place && !role && !note && !signaturePngBase64) continue;
      out.push({ firstName, lastName, place, role, note, signaturePngBase64 });
    }
    return out;
  }

  onSave(): void {
    if (!this.entry || this.isSubmitting || !this.canEditForm) return;
    this.isSubmitting = true;
    this.errorMessage = null;
    this.api.update(this.entry.id, this.buildPayload()).subscribe({
      next: (updated) => {
        this.entry = updated;
        this.isSubmitting = false;
        this.toast.showSuccess(
          updated.isLocked
            ? 'Dodatek uložen. Platná uzamčená verze zůstala beze změny.'
            : 'Uloženo.',
        );
        this.saved.emit(updated);
      },
      error: (err) => {
        this.isSubmitting = false;
        this.errorMessage = err?.message || 'Nepodařilo se uložit záznam.';
      },
    });
  }

  /** Potvrzení = uložit aktuální změny + podpis + uzamčení (PDF na Drive). */
  onConfirm(): void {
    if (!this.entry || this.isConfirming || !this.showConfirmSection) return;
    if (!this.sigPad || this.sigPad.isEmpty()) {
      this.errorMessage = 'Před uzavřením dne zapište podpis.';
      return;
    }
    this.errorMessage = null;
    this.clearConfirmAutoClose();
    const additional = this.buildAdditionalSignaturesForConfirm();
    if (additional === null) return;
    const signaturePngBase64 = this.sigPad.toPngDataUrl();
    this.isConfirming = true;
    const entryId = this.entry.id;
    this.api
      .confirm(entryId, {
        signaturePngBase64,
        draft: this.buildPayload(),
        ...(additional.length ? { additionalSignatures: additional } : {}),
      })
      .subscribe({
        next: (confirmed) => {
          this.entry = confirmed;
          this.form.disable({ emitEvent: false });
          this.isConfirming = false;
          this.toast.showSuccess(
            'Deník byl potvrzen. PDF se generuje na pozadí. Okno se za chvíli zavře.',
          );
          this.confirmed.emit(confirmed);
          this.clearConfirmAutoClose();
          this.confirmAutoCloseHandle = setTimeout(() => {
            this.confirmAutoCloseHandle = null;
            this.closeEvent.emit();
          }, DiaryEntryModalComponent.CONFIRM_AUTO_CLOSE_MS);
        },
        error: (err) => {
          this.isConfirming = false;
          this.errorMessage = err?.message || 'Potvrzení selhalo.';
        },
      });
  }

  /** X / Escape / backdrop — always dismiss (in-flight save/confirm continues; do not gate on validity). */
  onClose(): void {
    this.clearConfirmAutoClose();
    this.closeEvent.emit();
  }

  formatDate(d?: string | null): string {
    return formatDiaryEntryDateCs(d, 'long');
  }

  formatDateTime(d?: string | null): string {
    if (!d) return '—';
    const date = new Date(d);
    if (!Number.isFinite(date.getTime())) return '—';
    return new Intl.DateTimeFormat('cs-CZ', {
      timeZone: 'Europe/Prague',
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(date);
  }
}

function toTrim(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function toFiniteNumber(value: unknown): number | undefined {
  if (value === '' || value == null) return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : undefined;
}
